import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { RefreshCw, ShieldCheck, Store, XCircle, CheckCircle2 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Super Admin — Sales Channels oversight.
 * Cross-tenant table showing every connected sales channel, its push
 * status, and quick toggles. Drill-down to the push log per row.
 */
const SuperSalesChannelsPage = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushHealth, setPushHealth] = useState(null);
  const [filter, setFilter] = useState('');

  const token = () => localStorage.getItem('super_admin_token') || localStorage.getItem('token');

  const load = async () => {
    setLoading(true);
    try {
      const [listResp, healthResp] = await Promise.all([
        axios.get(`${API_BASE_URL}/sales-channels`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
        axios.get(`${API_BASE_URL}/sales-channels/push-health`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
      ]);
      setChannels(listResp.data.channels || []);
      setPushHealth(healthResp.data || null);
    } catch (e) {
      toast.error('Failed to load sales channels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (id, field, value) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/sales-channels/${id}`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      toast.success(`Updated ${field}`);
      load();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const migrate = async () => {
    try {
      const r = await axios.post(
        `${API_BASE_URL}/sales-channels/migrate-shopify-stores`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      toast.success(`Migration created ${r.data.created} channel(s)`);
      load();
    } catch (e) {
      toast.error('Migration failed');
    }
  };

  const filtered = channels.filter(c =>
    !filter
    || (c.name || '').toLowerCase().includes(filter.toLowerCase())
    || (c.channel_type || '').toLowerCase().includes(filter.toLowerCase())
    || (c.company_id || '').toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="p-8 space-y-6" data-testid="super-sales-channels-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-wt-text-primary flex items-center gap-2">
            <Store className="h-6 w-6" /> Sales Channels
          </h1>
          <p className="text-sm text-wt-text-muted mt-1">
            Cross-tenant oversight of every connected sales channel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="super-sc-filter"
            placeholder="Filter by tenant, channel, name..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-64"
          />
          <Button variant="outline" onClick={load} data-testid="super-sc-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={migrate} data-testid="super-sc-migrate">
            <ShieldCheck className="h-4 w-4 mr-2" /> Backfill Shopify
          </Button>
        </div>
      </div>

      {/* 24h push health */}
      {pushHealth && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Pushes (24h)', value: pushHealth.total, colour: 'text-wt-text-primary' },
            { label: 'Succeeded', value: pushHealth.succeeded, colour: 'text-green-400' },
            { label: 'Failed', value: pushHealth.failed, colour: 'text-amber-400' },
            { label: 'Permanent failures', value: pushHealth.permanent_failures, colour: 'text-red-400' },
          ].map((c) => (
            <div key={c.label} className="border border-wt-border rounded-xl p-4 bg-wt-card"
                 data-testid={`super-sc-stat-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="text-xs text-wt-text-muted">{c.label}</div>
              <div className={`text-2xl font-bold mt-1 ${c.colour}`}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="border border-wt-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-wt-bg">
            <tr className="text-left text-wt-text-muted">
              <th className="p-3 font-medium">Tenant</th>
              <th className="p-3 font-medium">Channel</th>
              <th className="p-3 font-medium">Name</th>
              <th className="p-3 font-medium text-center">Active</th>
              <th className="p-3 font-medium text-center">Push</th>
              <th className="p-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-wt-text-muted">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-wt-text-muted">No channels yet. Click "Backfill Shopify" to seed from existing Shopify stores.</td></tr>
            ) : filtered.map((c) => (
              <tr key={c.id} className="border-t border-wt-border hover:bg-wt-subtle/50" data-testid={`super-sc-row-${c.id}`}>
                <td className="p-3 font-mono text-xs text-wt-text-muted">{c.company_id}</td>
                <td className="p-3">
                  <Badge variant="outline" className="capitalize">{c.channel_type}</Badge>
                </td>
                <td className="p-3 text-wt-text-primary">{c.name}</td>
                <td className="p-3 text-center">
                  <Switch
                    checked={c.is_active}
                    onCheckedChange={(v) => toggle(c.id, 'is_active', v)}
                    data-testid={`super-sc-toggle-active-${c.id}`}
                  />
                </td>
                <td className="p-3 text-center">
                  <Switch
                    checked={c.push_inventory_enabled}
                    onCheckedChange={(v) => toggle(c.id, 'push_inventory_enabled', v)}
                    data-testid={`super-sc-toggle-push-${c.id}`}
                  />
                </td>
                <td className="p-3 text-xs text-wt-text-muted">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SuperSalesChannelsPage;
